import _ from 'lodash';
import productTypeGroupApi from '../../api/productTypeGroup';

export default {
    namespaced: true,
    state: {
        nestedList: [],
        productTypeGroups: [],
        initialProductTypeGroups: [],
        syncState: 'idle', // idle, running, done, fail
    },
    mutations: {
        SET_NESTED_LIST(state, list) {
            state.nestedList = list;
        },
        SET_PRODUCT_TYPE_GROUPS(state, groups) {
            state.productTypeGroups = _.cloneDeep(groups); // Deep copy for reactivity
        },
        SET_INITIAL_PRODUCT_TYPE_GROUPS(state, groups) {
            state.initialProductTypeGroups = _.cloneDeep(groups);
        },
        SET_SYNC_STATE(state, syncState) {
            state.syncState = syncState;
        },
    },
    actions: {
        async loadFlatData({ commit }) {
            try {
                const response = await productTypeGroupApi.list();
                const productTypeGroups = response.data;
                commit('SET_PRODUCT_TYPE_GROUPS', productTypeGroups);
                commit('SET_INITIAL_PRODUCT_TYPE_GROUPS', productTypeGroups);
            } catch (error) {
                console.error(
                    'loadFlatData - Failed to load product type groups:',
                    error
                );
                throw error;
            }
        },
        initializeTree({ commit, state }) {
            const productTypeGroups = state.productTypeGroups;
            const map = {};
            productTypeGroups.forEach((group) => {
                map[group.id] = {
                    id: group.id,
                    name: group.name,
                    parentId: group.parentId,
                    imagePathS3: group.imagePathS3,
                    sortOrder: group.sortOrder,
                    items: [],
                };
            });
            const tree = [];
            productTypeGroups.forEach((group) => {
                if (group.parentId && map[group.parentId]) {
                    map[group.parentId].items.push(map[group.id]);
                } else {
                    tree.push(map[group.id]);
                }
            });
            const sorter = (a, b) =>
                (a.sortOrder || Infinity) - (b.sortOrder || Infinity);
            tree.sort(sorter);
            Object.values(map).forEach((group) => group.items.sort(sorter));
            commit('SET_NESTED_LIST', tree);
        },
        updateTree({ commit }, { newList }) {
            if (!Array.isArray(newList) || newList.some((item) => !item.id)) {
                console.error('updateTree - Invalid newList format:', newList);
                return;
            }
            const deduplicateTree = (items, seenIds = new Set()) => {
                const result = [];
                for (const item of items) {
                    if (!seenIds.has(item.id)) {
                        seenIds.add(item.id);
                        const newItem = { ...item, items: [] };
                        if (item.items && item.items.length) {
                            newItem.items = deduplicateTree(
                                item.items,
                                seenIds
                            );
                        }
                        result.push(newItem);
                    } else {
                        console.warn(
                            `updateTree - Duplicate ID ${item.id} in newList, skipping.`
                        );
                    }
                }
                return result;
            };
            const dedupedList = deduplicateTree(newList);
            const updateParentIds = (items, parentId = null) => {
                items.forEach((item, index) => {
                    item.parentId = parentId;
                    item.sortOrder = index + 1; // Update sortOrder based on position
                    if (item.items && item.items.length) {
                        updateParentIds(item.items, item.id);
                    }
                });
            };
            updateParentIds(dedupedList);
            commit('SET_NESTED_LIST', dedupedList);
            this.dispatch('productTypeGroups/setFlatData');
        },
        setFlatData({ commit, state, dispatch }) {
            const updatedList = state.nestedList;
            const updatedGroups = [];
            const seenIds = new Set();
            const processGroup = (group, parentId = null, index) => {
                if (seenIds.has(group.id)) {
                    console.warn(
                        `setFlatData - Duplicate group ID ${group.id} at parentId ${parentId}, index ${index}, skipping.`
                    );
                    return;
                }
                seenIds.add(group.id);
                // Merge with original productTypeGroups to retain translations and productTypes
                const originalGroup =
                    state.productTypeGroups.find((g) => g.id === group.id) ||
                    {};
                updatedGroups.push({
                    id: group.id,
                    name: group.name,
                    parentId,
                    imagePathS3: group.imagePathS3,
                    sortOrder: index + 1,
                    translations: originalGroup.translations || [],
                    productTypes: originalGroup.productTypes || [],
                });
                if (group.items && group.items.length) {
                    group.items.forEach((child, childIndex) => {
                        processGroup(child, group.id, childIndex);
                    });
                }
            };
            updatedList.forEach((group, index) =>
                processGroup(group, null, index)
            );
            commit('SET_PRODUCT_TYPE_GROUPS', updatedGroups);
            dispatch('debouncedPersistHierarchy');
        },
        debouncedPersistHierarchy: _.debounce(function ({ dispatch, commit }) {
            commit('SET_SYNC_STATE', 'running');
            dispatch('persistHierarchy');
        }, 500),
        async persistHierarchy({ commit, getters, dispatch }) {
            try {
                commit('SET_SYNC_STATE', 'running');
                const payload = getters.getHierarchyPayload;
                if (!payload.length) {
                    commit('SET_SYNC_STATE', 'idle');
                    return;
                }
                const response = await productTypeGroupApi.bulkUpdate(payload);
                // Update initial state to current state
                commit('SET_INITIAL_PRODUCT_TYPE_GROUPS', response.data);
                commit('SET_PRODUCT_TYPE_GROUPS', response.data);
                commit('SET_SYNC_STATE', 'done');
                dispatch('resetSyncState');
                dispatch('initializeTree');
            } catch (error) {
                console.error(
                    'persistHierarchy - Failed to persist hierarchy:',
                    error
                );
                commit('SET_SYNC_STATE', 'fail');
                dispatch('resetSyncState');
                throw error;
            }
        },
        async resetSyncState({ commit }) {
            await new Promise((resolve) => setTimeout(resolve, 2000)); // Wait 2 seconds
            commit('SET_SYNC_STATE', 'idle');
        },
    },
    getters: {
        getProductTypeGroups: (state) => {
            return state.productTypeGroups;
        },
        getHierarchyPayload: (state) => {
            const initialMap = new Map(
                state.initialProductTypeGroups.map((item) => [item.id, item])
            );
            const currentMap = new Map(
                state.productTypeGroups.map((item) => [item.id, item])
            );
            const changedIds = new Set();
            for (const [id, currentItem] of currentMap) {
                const initialItem = initialMap.get(id);
                if (initialItem) {
                    if (
                        currentItem.parentId !== initialItem.parentId ||
                        currentItem.sortOrder !== initialItem.sortOrder
                    ) {
                        changedIds.add(id);
                    }
                }
            }
            const payload = Array.from(changedIds).map((id) => {
                const currentItem = currentMap.get(id);
                return {
                    id,
                    parentId: currentItem.parentId,
                    sortOrder: currentItem.sortOrder,
                };
            });
            return payload;
        },
        getSyncState: (state) => {
            return state.syncState;
        },
    },
};
